import { useState } from "react";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useUser } from "@/context/UserContext";
import { INTERACTION_TYPES } from "@/data/investorOptions";

const fields = [
  { name: "interaction_type", label: "Interaction type", type: "select", placeholder: "Type", options: INTERACTION_TYPES, required: true },
  { name: "date", label: "Date", type: "date" },
  { name: "contact_person", label: "Contact person", type: "text", placeholder: "Who did we speak with?" },
  { name: "summary", label: "Summary", type: "textarea", placeholder: "What happened?", required: true },
  { name: "outcome", label: "Outcome", type: "textarea", placeholder: "Result of this interaction" },
  { name: "next_action", label: "Next action", type: "textarea", placeholder: "Agreed next step" },
  { name: "next_follow_up_date", label: "Next follow-up date", type: "date" },
  { name: "notes", label: "Notes", type: "textarea", placeholder: "Any other detail" },
];

// Handles add/edit of an interaction, and (on add) offers to sync next action / follow-up to the investor.
export const InteractionModal = ({ open, onOpenChange, investor, editing, onSaved }) => {
  const { currentUser } = useUser();
  const [pendingSync, setPendingSync] = useState(null);

  const handleSubmit = async (values) => {
    const payload = { ...values, investor_id: investor.id, author: values.author || currentUser.name };
    const saved = await onSaved.save(payload, editing?.id);
    // if new interaction carries a next action / follow-up, offer to update the investor
    if (!editing?.id && (values.next_action || values.next_follow_up_date)) {
      setPendingSync({ next_action: values.next_action, next_follow_up_date: values.next_follow_up_date });
    }
    return saved;
  };

  const applySync = async () => {
    const patch = {};
    if (pendingSync.next_action) patch.next_action = pendingSync.next_action;
    if (pendingSync.next_follow_up_date) patch.next_follow_up_date = pendingSync.next_follow_up_date;
    await onSaved.syncInvestor(patch);
    setPendingSync(null);
  };

  return (
    <>
      <EntryModal
        open={open}
        onOpenChange={onOpenChange}
        title={editing?.id ? "Edit Interaction" : "Add Interaction"}
        description={`${editing?.id ? "Editing" : "Logging as"} ${currentUser.name} · ${investor?.name || ""}`}
        submitLabel={editing?.id ? "Save Changes" : "Add Interaction"}
        fields={fields}
        initialValues={editing || { interaction_type: "Research note", date: new Date().toISOString().slice(0, 10) }}
        onSubmit={handleSubmit}
      />
      <ConfirmDialog
        open={!!pendingSync}
        onOpenChange={(o) => !o && setPendingSync(null)}
        title="Update the investor record?"
        description="This interaction includes a new next action / follow-up date. Apply it to the investor's record?"
        confirmLabel="Update investor"
        onConfirm={applySync}
      />
    </>
  );
};
